import React, { useEffect, useState } from "react";
import { Control, FieldErrors } from "react-hook-form";
import {
    mapToDropdownOption,
    SearchDropdown,
    DropdownOption,
    ensure,
    FormSingleSelectList,
    Optional,
    Stack,
    mapToCodedProperty,
    ValidationResult,
    getById
} from "sprint-common-web";

import { SuggestContentListByTitleResponseTvSeries } from "../../../../../common/api/catalogue-api/contents-api/SuggestContentListByTitleResponse";
import { CreateContentVersionButton } from "../../../../../common/components/content-version/CreateContentVersionButton";
import { CrossTypeContentSearchItem } from "../../../../../common/components/form/CrossTypeContentSearch";
import { ContentVersion } from "../../../../../common/types/ContentReference";
import { ContentReferenceType } from "../../../../../common/types/ContentReferenceType";
import { isTrailerVersionType } from "../../../../../common/types/TrailerContentVersion";
import { getEpisodeText } from "../../../../../common/utils/getEpisodeText";
import { renderBrandedContentVersionText } from "../../../../../common/utils/renderContentVersionText";
import { ensureTvAssociateDetails } from "../../../../../content-ingestion/common/content/types/ContentAssociationDetails";
import { renderTrailerVersionInfo } from "../../../../../content-ingestion/common/content/utils/renderTrailerVersionInfo";
import { useCreateOrder } from "../../common/provider/useCreateOrder";
import { OrderEpisodeVersionItem } from "../../types/OrderContentVersionItem";
import { createOrderEpisodeItemValidator } from "./OrderEpisodeItemValidator";

interface OrderEpisodeVersionSelectorProps {
    control: Control;
    errors: FieldErrors;
    dependsOn: string[];
}

export const OrderEpisodeVersionSelector: React.FC<OrderEpisodeVersionSelectorProps> = ({
    control,
    errors,
    dependsOn
}: OrderEpisodeVersionSelectorProps) => {
    const { contentDetails, contentVersion, setEpisodeVersion, setContentDetails } = useCreateOrder();

    const [seasonId, setSeasonId] = useState<string>();
    const [episodeId, setEpisodeId] = useState<string>();

    useEffect(() => {
        if (contentVersion && contentVersion.type === ContentReferenceType.TvSeries) {
            setSeasonId(contentVersion.season.id);
            setEpisodeId(contentVersion.episode.id);
        }
    }, [contentVersion]);

    const validator = createOrderEpisodeItemValidator();

    const validateVersion = (itemId: string): ValidationResult => {
        ensure(contentDetails);
        ensureTvAssociateDetails(contentDetails);

        const season = contentDetails.seasons.find((s) => s.id === seasonId);
        const episode = season?.episodes.find((e) => e.id === episodeId);

        const versions = episode?.versions || [];

        const versionToValidate = getById(versions, itemId);

        return validator.validateVersion(versionToValidate, versions);
    };

    const onContentVersionSelect = (selectedVersionId: string) => {
        ensure(contentDetails);
        ensureTvAssociateDetails(contentDetails);

        const selectedSeason = contentDetails.seasons.find((season) => season.id === seasonId);
        const selectedEpisode = selectedSeason?.episodes.find((episode) => episode.id === episodeId);
        const selectedVersion = selectedEpisode?.versions.find((version) => version.id === selectedVersionId);

        if (!selectedSeason || !selectedEpisode || !selectedVersion) {
            setEpisodeVersion(undefined);
            return;
        }

        setEpisodeVersion(
            getSetEpisodeVersionParams({
                seasonId,
                episodeId,
                contentDetails,
                version: {
                    distributor: {
                        id: selectedVersion.distributor.id,
                        name: selectedVersion.distributor.name
                    },
                    brandings: selectedVersion.brandings?.map((item) => mapToCodedProperty(item)),
                    id: selectedVersion.id,
                    type: selectedVersion.type,
                    buzzId: selectedVersion.buzzVersion?.id
                }
            })
        );
    };

    const seasonOptions = getSeasonOptions({ contentDetails });
    const episodeOptions = getEpisodeOptions({ contentDetails, seasonId });
    const episodeVersionOptions = getEpisodeVersionOptions({ contentDetails, seasonId, episodeId });

    const shouldRenderTrailerInfo = (itemId: string) => {
        ensure(contentDetails);
        ensureTvAssociateDetails(contentDetails);

        const season = contentDetails.seasons.find((s) => s.id === seasonId);
        const episode = season?.episodes.find((e) => e.id === episodeId);

        const versions = episode?.versions || [];

        if (!versions.length) return false;

        const versionToCheck = versions.find((v) => v.id === itemId);

        return isTrailerVersionType(versionToCheck?.type);
    };

    const renderAdditionalInfo = (itemId: string) => {
        if (!shouldRenderTrailerInfo(itemId)) {
            return <></>;
        }

        return renderTrailerVersionInfo({});
    };

    const createContentVersionButtonView = contentDetails && episodeId && (
        <Stack style={{ marginLeft: "5px" }}>
            <CreateContentVersionButton
                content={{
                    type: ContentReferenceType.TvSeries,
                    tvSeriesId: contentDetails.id,
                    episodeId
                }}
                onContentVersionCreated={(newVersion) => {
                    ensureTvAssociateDetails(contentDetails);

                    setContentDetails({
                        ...contentDetails,
                        seasons: contentDetails.seasons.map((season) => {
                            return {
                                ...season,
                                episodes: season.episodes.map((episode) => {
                                    return {
                                        ...episode,
                                        versions: [newVersion, ...episode.versions]
                                    };
                                })
                            };
                        })
                    });

                    setEpisodeVersion(
                        getSetEpisodeVersionParams({
                            seasonId,
                            episodeId,
                            contentDetails,
                            version: {
                                distributor: {
                                    id: newVersion.distributor.id,
                                    name: newVersion.distributor.name
                                },
                                brandings: newVersion.brandings?.map((item) => mapToCodedProperty(item)),
                                id: newVersion.id,
                                type: newVersion.type
                            }
                        })
                    );
                }}
            />
        </Stack>
    );

    const actionButtons: JSX.Element[] = [];

    if (createContentVersionButtonView) {
        actionButtons.push(createContentVersionButtonView);
    }

    return (
        <>
            <SearchDropdown
                label="Season*"
                placeholder="Search and select season"
                name="contentSeasonId"
                selectedKey={seasonId}
                options={seasonOptions}
                onChange={(value: DropdownOption) => {
                    if (!value?.key) {
                        setSeasonId(undefined);
                        setEpisodeVersion(undefined);

                        return;
                    }
                    setSeasonId(value.key);
                }}
                disabled={!seasonOptions.length}
                testId="create-order-episode-selector-season"
                errors={errors}
            />
            <SearchDropdown
                label="Episode*"
                placeholder="Search and select episode"
                name="contentEpisodeId"
                selectedKey={episodeId}
                options={episodeOptions}
                onChange={(value: DropdownOption) => {
                    if (!value?.key) {
                        setEpisodeId(undefined);
                        setEpisodeVersion(undefined);

                        return;
                    }
                    setEpisodeId(value.key);
                }}
                disabled={!episodeOptions.length}
                testId="create-order-episode-selector-episode"
                errors={errors}
            />
            <FormSingleSelectList
                items={episodeVersionOptions}
                onChange={onContentVersionSelect}
                onItemValidating={validateVersion}
                renderAdditionalInfo={renderAdditionalInfo}
                control={control}
                errors={errors}
                rules={{ required: { value: true, message: "Select version" } }}
                actionButtons={actionButtons}
                name="contentVersionId"
                label="Episode version"
                disabled={!episodeVersionOptions.length}
                minHeight={150}
                maxHeight={150}
                dependsOn={dependsOn}
                testId="create-order-episode-selector-episode-version"
            />
        </>
    );
};

const getSeasonOptions = ({ contentDetails }: { contentDetails?: CrossTypeContentSearchItem }): DropdownOption[] => {
    if (!contentDetails) {
        return [];
    }

    ensureTvAssociateDetails(contentDetails);

    return contentDetails.seasons.map((season) => mapToDropdownOption(season));
};

const getEpisodeOptions = ({
    contentDetails,
    seasonId
}: {
    contentDetails?: CrossTypeContentSearchItem;
    seasonId?: string;
}): DropdownOption[] => {
    if (!contentDetails || !seasonId) {
        return [];
    }

    ensureTvAssociateDetails(contentDetails);

    const season = contentDetails.seasons.find((s) => s.id === seasonId);

    if (!season) {
        return [];
    }

    return season.episodes.map((episode) => mapToDropdownOption(episode, getEpisodeText));
};

const getEpisodeVersionOptions = ({
    contentDetails,
    seasonId,
    episodeId
}: {
    contentDetails?: CrossTypeContentSearchItem;
    seasonId?: string;
    episodeId?: string;
}): DropdownOption[] => {
    if (!contentDetails || !seasonId || !episodeId) {
        return [];
    }

    ensureTvAssociateDetails(contentDetails);

    const season = contentDetails.seasons.find((s) => s.id === seasonId);

    if (!season) {
        return [];
    }

    const episode = season.episodes.find((e) => e.id === episodeId);

    if (!episode) {
        return [];
    }

    return episode.versions.map((version) =>
        mapToDropdownOption(version, () => renderBrandedContentVersionText(version))
    );
};

const getSetEpisodeVersionParams = ({
    contentDetails,
    version,
    seasonId,
    episodeId
}: {
    contentDetails: SuggestContentListByTitleResponseTvSeries;
    version: ContentVersion;
    seasonId?: string;
    episodeId?: string;
}): Optional<OrderEpisodeVersionItem> => {
    const selectedSeason = contentDetails.seasons.find((season) => season.id === seasonId);
    const selectedEpisode = selectedSeason?.episodes.find((episode) => episode.id === episodeId);

    if (!selectedSeason || !selectedEpisode) {
        return undefined;
    }

    return {
        type: ContentReferenceType.TvSeries,
        subtype: contentDetails.subtype,
        tvSeries: {
            id: contentDetails.id,
            title: contentDetails.title,
            buzzId: contentDetails.buzzId
        },
        season: {
            id: selectedSeason.id,
            name: selectedSeason.name,
            buzzId: selectedSeason.buzzId
        },
        episode: {
            id: selectedEpisode.id,
            title: selectedEpisode.title,
            number: selectedEpisode.number,
            buzzId: selectedEpisode.buzz?.id
        },
        version
    };
};
